import useSocketReconnect from '../../utils/useSocketReconnect';
import { useCallback, useEffect, useMemo, useState } from "react";
import Controller from "./Controller";
import parseSVG from './parseSVG'
import PissarraEditor from "./PissarraEditor";
import PrintableEditor from "./PrintableEditor";
import PaintColorPopup from "./PaintColorPopup";
import ProvesTab from "./tabs/ProvesTab";
import PlantillesTab from "./tabs/PlantillesTab";
import CrearTab from "./tabs/CrearTab";
import PapereraTab from "./tabs/PapereraTab";
import { fetchAPI } from "../../utils/utils";
import Pressable from "../other/Pressable";
import UserInfo from "../login/UserInfo";
import HeaderTabs from "../interface/HeaderTabs";
import "../gestio/GestioApp.css";
import "./table/EditorTable.css";

const formatDurations = data => {
    let dict = {}
    const canvis = data.split('\n')
    for (const canvi of canvis) {
        if (canvi.split('=').length !== 2) continue
        const [prova, duration] = canvi.split('=')
        dict[prova] = parseInt(duration)
    }
    return dict;
};

export const addAddons = (jsonOutput, addons) => {
    // Add add-ons
    if (!jsonOutput) return null
    const withAddons = JSON.parse(JSON.stringify(jsonOutput))

    // Escaletes
    const escaletes = addons?.escaletes || {}
    Object.entries(escaletes).forEach(([from, to]) => {
        if (!withAddons[from]) return
        withAddons[from].escala_a = to
    })

    // Trepitjacions
    const trepitjacions = addons?.trepitjacions || {}
    Object.entries(trepitjacions).forEach(([from, to]) => {
        if (!withAddons[from]) return
        withAddons[from].trepitja_a = to
    })

    return withAddons
}

function EditorApp(props) {
    const [svg, setSvg] = useState(null);
    const [jsonOutput, setJsonOutput] = useState(null);
    const [addons, setAddons] = useState(null);
    const [pz, setPanzoom] = useState(null);
    const { socket } = props;

    const [swiper, setSwiper] = useState(null);
    const [extended, setExtended] = useState(false);

    const [mode, setMode] = useState(null);
    const [submode, setSubmode] = useState(null);
    const [snapEnabled, setSnapEnabled] = useState(true);
    const [paintColor, setPaintColor] = useState('#ffffff');
    const [colorPopupClosed, setColorPopupClosed] = useState(true);

    const [caixaSelected, setCaixaSelected] = useState(-1);
    const [duplicating, setDuplicating] = useState(null);
    const [choosingTrepitjat, setChoosingTrepitjat] = useState(null);
    const [choosingEscaleta, setChoosingEscaleta] = useState(null);

    const [bundleInfo, setBundleInfo] = useState(null);
    const [availableBundles, setAvailableBundles] = useState([])

    const params = window.location.pathname.split('/').filter(part => part != '');
    const selectedCastellPart = params[1] && !['new', 'bundle', 'proves', 'amagades', 'plantilles', 'crear', 'paperera'].includes(params[1]) ? params[1] : null;
    const selectedBundle = params[1] === 'bundle' && params[2] ? params[2] : null;

    const [availablePlantilles, setAvailablePlantilles] = useState([]);
    const [bundlesLoaded, setBundlesLoaded] = useState(false);
    const [plantillesLoaded, setPlantillesLoaded] = useState(false);
    const [notification, setNotification] = useState(null);

    const [plantillaDefaultName, setPlantillaDefaultName] = useState(null);
    const [isFocused, setIsFocused] = useState(false);

    const [pestanya, setPestanya] = useState(null)
    const tabs = useMemo(() => bundleInfo?.parts ? Object.keys(bundleInfo.parts) : null, [bundleInfo]);

    const [activeTab, setActiveTab] = useState('proves');

    // Proves tab state
    const [durations, setDurations] = useState({});
    useSocketReconnect(socket, () => {
        socket.emit('.request_durations');
        socket.emit('.request_plantilles');
        // Recover missing initial data without replacing an editor's local changes.
        if (selectedBundle && !bundleInfo) socket.emit('.request_bundle', selectedBundle);
        if (selectedBundle && !addons) socket.emit('.request_addons', selectedBundle);
        if (jsonOutput === null && !svg) {
            if (selectedBundle) socket.emit('.load_bundle', selectedBundle);
            if (selectedCastellPart) socket.emit('.load_json', selectedCastellPart);
        }
    });


    const editorTabs = ['proves', 'plantilles', 'crear', 'paperera'];

    const getTabFromPath = () => {
        const path = window.location.pathname;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];

        if (editorTabs.includes(lastPart)) {
            return lastPart;
        }

        return 'proves';
    };

    const changeTab = (tab) => {
        setActiveTab(tab);

        const currentPath = window.location.pathname;
        const basePath = currentPath.replace(/\/(proves|amagades|plantilles|crear|paperera)$/, '');
        const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;

        window.history.pushState({ tab }, '', `${normalizedBasePath}${tab}`);
    };

    const parser = new DOMParser();
    const svgData = parser.parseFromString(svg, "image/svg+xml");

    const json = useMemo(() => addAddons(jsonOutput, addons), [jsonOutput, addons]);

    const [selectedIds, setSelectedIds] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(0);

    const setJsonOutputWrapper = (newJson, saveToHistory=true) => {
        if (typeof newJson === 'function') {
            setJsonOutput(prev => {
                const calculated = newJson(prev);
                if (saveToHistory) {
                    const newHistory = history.slice(0, historyStep + 1);
                    newHistory.push(calculated);
                    setHistory(newHistory);
                    setHistoryStep(newHistory.length - 1);
                }
                return calculated;
            });
        } else {
            if (saveToHistory) {
                const newHistory = history.slice(0, historyStep + 1);
                newHistory.push(newJson);
                setHistory(newHistory);
                setHistoryStep(newHistory.length - 1);
            }
            setJsonOutput(newJson);
        }
    }

    const undo = () => {
        if (historyStep > 0) {
            setHistoryStep(prev => prev - 1);
            setJsonOutput(history[historyStep - 1]);
        }
    }

    const redo = () => {
        if (historyStep < history.length - 1) {
            setHistoryStep(prev => prev + 1);
            setJsonOutput(history[historyStep + 1]);
        }
    }

    useEffect(() => {
        if (jsonOutput && history.length === 0) {
            setHistory([jsonOutput]);
        }
    }, [jsonOutput]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    redo();
                } else {
                    undo();
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [history, historyStep]);

    const exports = {
        availableBundles,
        setAvailableBundles,
        'isFocused': isFocused,
        'setIsFocused': setIsFocused,
        'socket': socket,
        'svg': svg,
        'setSvg': setSvg,
        'setJsonOutput': setJsonOutputWrapper,
        'selectedIds': selectedIds,
        'setSelectedIds': setSelectedIds,
        'undo': undo,
        'redo': redo,
        'canUndo': historyStep > 0,
        'canRedo': historyStep < history.length - 1,
        'setPanzoom': setPanzoom,
        'setSwiper': setSwiper,
        'setExtended': setExtended,
        'swiper': swiper,
        'pz': pz,
        'extended': extended,
        'jsonOutput': jsonOutput,
        'json': json,
        'mode': mode,
        'setMode': setMode,
        'submode': submode,
        'setSubmode': setSubmode,
        'snapEnabled': snapEnabled,
        'setSnapEnabled': setSnapEnabled,
        'paintColor': paintColor,
        'setPaintColor': setPaintColor,
        'colorPopupClosed': colorPopupClosed,
        'setColorPopupClosed': setColorPopupClosed,
        'caixaSelected': caixaSelected,
        'setCaixaSelected': setCaixaSelected,
        'duplicating': duplicating,
        'setDuplicating': setDuplicating,
        'choosingTrepitjat': choosingTrepitjat,
        'setChoosingTrepitjat': setChoosingTrepitjat,
        'choosingEscaleta': choosingEscaleta,
        'setChoosingEscaleta': setChoosingEscaleta,
        'selectedCastellPart': selectedCastellPart,
        'selectedBundle': selectedBundle,
        'bundleInfo': bundleInfo,
        'notification': notification,
        'setNotification': setNotification,
        'pestanya': pestanya,
        'setPestanya': setPestanya,
        'tabs': tabs,
        'plantillaDefaultName': plantillaDefaultName,
        'addons': addons,
        'setAddons': setAddons,
        ...props
    }

    useEffect(() => {
        document.title = `Plantilles - Aleta`;

        const tabFromPath = getTabFromPath();
        setActiveTab(tabFromPath);

        const handlePopState = () => {
            setActiveTab(getTabFromPath());
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        if (svg) {
            setJsonOutput(parseSVG(svgData));
        } else {
            setJsonOutput(null);
        }
    }, [svg])

    useEffect(() => {
        setAddons(null);
        setBundleInfo(null);
        const onJson = data => setJsonOutput(data);
        const onBundle = data => {
            if (data.id === selectedBundle) {
                setBundleInfo(data.bundle)
                socket.emit('.request_addons', selectedBundle)
            }
        };
        const onAddons = data => {
            // The logo can request this bundle too; hydrate only once per selection.
            if (data.id === selectedBundle) setAddons(current => current ?? data.addons);
        };

        socket.on('.loaded_json', onJson);
        socket.on('.bundle', onBundle);
        socket.on('.addons', onAddons);
        if (selectedBundle) socket.emit('.request_bundle', selectedBundle);
        if (selectedBundle) socket.emit('.load_bundle', selectedBundle);
        if (selectedCastellPart) socket.emit('.load_json', selectedCastellPart);

        return () => {
            socket.off('.loaded_json', onJson);
            socket.off('.addons', onAddons);
            socket.off('.bundle', onBundle);
        }
    }, [selectedBundle, selectedCastellPart, socket]);

    useEffect(() => {
        if (selectedBundle) {
            setPestanya(tabs?.[0] || 'Pinya')
        }
    }, [selectedBundle, tabs?.length])

    // Durations + bundles (moved from BundleList)
    useEffect(() => {
        socket.emit('.request_durations');
        fetchAPI('/bundles', data => { setAvailableBundles(data); setBundlesLoaded(true); });

        const onDurations = data => setDurations(formatDurations(data));
        const onDurationsChanged = (prova, duration) => {
            setDurations(prev => ({...prev, [prova]: duration}));
        };
        const onBundles = (data) => { setAvailableBundles(data); setBundlesLoaded(true); };
        const onBundleCreated = () => fetchAPI('/bundles', setAvailableBundles, false);
        const onBundleEdited = () => fetchAPI('/bundles', setAvailableBundles, false);
        const onErrorBundle = error => alert(error);

        socket.on('.durations', onDurations);
        socket.on('.durations_changed', onDurationsChanged);
        socket.on('.bundles', onBundles);
        socket.on('.bundle_created', onBundleCreated);
        socket.on('.bundle_edited', onBundleEdited);
        socket.on('.error_bundle', onErrorBundle);

        return () => {
            socket.off('.durations', onDurations);
            socket.off('.durations_changed', onDurationsChanged);
            socket.off('.bundles', onBundles);
            socket.off('.bundle_created', onBundleCreated);
            socket.off('.bundle_edited', onBundleEdited);
            socket.off('.error_bundle', onErrorBundle);
        }
    }, [])

    const handleDurationChange = useCallback((bundleId, newDuration) => {
        setDurations(prev => ({...prev, [bundleId]: newDuration}));
    }, [])


    useEffect(() => {
        socket.emit('.request_plantilles');

        const onPlantilles = (data) => { setAvailablePlantilles(data); setPlantillesLoaded(true); };
        socket.on('.plantilles', onPlantilles);

        const onCreatedPlantilla = (nom) => {
            console.log(`Plantilla ${nom} creada correctament.`)
            socket.emit('.request_plantilles');
        }
        socket.on('.created_plantilla', onCreatedPlantilla)

        const onSavedPlantilla = (nom) => {
            console.log(`Plantilla ${nom} guardada correctament.`)
            socket.emit('.request_plantilles');
        }
        socket.on('.saved_plantilla', onSavedPlantilla)

        const onDuplicatePlantilla = (nom) => {
            console.log(`Plantilla ${nom} canviada correctament. L'antiga plantilla s'ha guardat en un backup.`)
            socket.emit('.request_plantilles');
        }
        socket.on('.duplicate_plantilla', onDuplicatePlantilla)

        const onRenamedPlantilla = () => {
            socket.emit('.request_plantilles');
            socket.emit('.request_bundles')
        }
        socket.on('.renamed_plantilla', onRenamedPlantilla)

        const onUpdatedPlantilles = () => socket.emit('.request_plantilles')
        socket.on('.updated_plantilles', onUpdatedPlantilles)

        const onErrorPlantilla = (error) => alert(error)
        socket.on('.error_plantilla', onErrorPlantilla)

        const onDeletedPlantilla = (nom) => {
            socket.emit('.request_plantilles');
            alert(`Plantilla ${nom} eliminada correctament. L'antiga plantilla s'ha guardat en un backup.`)
        }
        socket.on('.deleted_plantilla', onDeletedPlantilla)

        return () => {
            socket.off('.created_plantilla', onCreatedPlantilla);
            socket.off('.saved_plantilla', onSavedPlantilla);
            socket.off('.duplicate_plantilla', onDuplicatePlantilla);
            socket.off('.deleted_plantilla', onDeletedPlantilla);
            socket.off('.updated_plantilles', onUpdatedPlantilles);
            socket.off('.plantilles', onPlantilles);
            socket.off('.renamed_plantilla', onRenamedPlantilla);
            socket.off('.error_plantilla', onErrorPlantilla);
        }
    }, [])

    const renderTabContent = () => {
        if (activeTab === 'proves') {
            return (
                <ProvesTab
                    availableBundles={availableBundles}
                    durations={durations}
                    availablePlantilles={availablePlantilles}
                    socket={socket}
                    onDurationChange={handleDurationChange}
                    loading={!bundlesLoaded || !plantillesLoaded}
                    userId={props.userId}
                />
            );
        }

        if (activeTab === 'plantilles') {
            return (
                <PlantillesTab
                    availablePlantilles={availablePlantilles}
                    availableBundles={availableBundles}
                    socket={socket}
                    loading={!plantillesLoaded}
                    userId={props.userId}
                />
            );
        }

        if (activeTab === 'crear') {
            return (
                <CrearTab
                    setSvg={setSvg}
                    setPlantillaDefaultName={setPlantillaDefaultName}
                />
            );
        }

        if (activeTab === 'paperera') {
            return (
                <PapereraTab
                    availableBundles={availableBundles}
                    socket={socket}
                    editorExports={exports}
                    loading={!bundlesLoaded}
                />
            );
        }

        // Redirect old 'amagades' URL to paperera
        if (activeTab === 'amagades') {
            changeTab('paperera');
            return null;
        }

        return null;
    };

    return (
        <>
            {
                jsonOutput === null && selectedCastellPart === null && selectedBundle === null ?
                    <div style={{
                        minHeight: '100vh',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        overflowX: 'hidden',
                    }}>
                        <div className="editor-page-wrapper" style={{
                            maxWidth: "90%",
                            margin: '0 auto',
                            padding: '24px',
                            boxSizing: 'border-box',
                        }}>
                            <UserInfo {...exports} />
                            
                            <HeaderTabs {...exports} />

                            <Pressable 
                                style={{ 
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: '10px 16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 32,
                                    color: '#374151',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                }} 
                                className="boto-back" 
                                href='/'
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                    e.currentTarget.style.transform = 'translateX(-4px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                    e.currentTarget.style.transform = 'translateX(0)';
                                }}
                            >
                                ← Tornar a la pàgina principal
                            </Pressable>
                            
                            <h1 style={{
                                fontSize: 36,
                                fontWeight: 700,
                                color: '#111827',
                                marginBottom: 8,
                            }}>
                                Editor de Plantilles
                            </h1>
                            
                            <p style={{
                                fontSize: 18,
                                color: '#6b7280',
                                marginBottom: 24,
                                maxWidth: 600,
                            }}>
                                Crea i edita les plantilles per als teus castells
                            </p>

                            {/* Tab Navigation */}
                            <div className="page-layout">
                                <div className="navigation-tabs">
                                    <button
                                        className={`tab-button ${activeTab === 'proves' ? 'active' : ''}`}
                                        onClick={() => changeTab('proves')}
                                    >
                                        <i className="fas fa-cubes"></i> Proves
                                    </button>
                                    <button
                                        className={`tab-button ${activeTab === 'plantilles' ? 'active' : ''}`}
                                        onClick={() => changeTab('plantilles')}
                                    >
                                        <i className="fas fa-file-alt"></i> Plantilles
                                    </button>
                                    <button
                                        className={`tab-button ${activeTab === 'crear' ? 'active' : ''}`}
                                        onClick={() => changeTab('crear')}
                                    >
                                        <i className="fas fa-plus"></i> Crear plantilla
                                    </button>
                                    <button
                                        className={`tab-button ${activeTab === 'paperera' ? 'active' : ''}`}
                                        onClick={() => changeTab('paperera')}
                                    >
                                        <i className="fas fa-trash"></i> Paperera
                                    </button>
                                </div>

                                <div className="page-content">
                                    {renderTabContent()}
                                </div>
                            </div>

                            <div style={{ height: '50px' }}></div>
                        </div>
                    </div>
                : jsonOutput !== null ?
                    <>
                        <PissarraEditor {...exports} />
                        <PrintableEditor
                            json={json}
                            tabs={tabs}
                            mode={mode}
                            addons={addons}
                            bundleInfo={bundleInfo}
                            selectedBundle={selectedBundle}
                            selectedCastellPart={selectedCastellPart}
                            plantillaDefaultName={plantillaDefaultName}
                        />
                        <Controller {...exports} />
                        <PaintColorPopup {...exports} />
                    </>
                : 
                    <></>
            }
        </>
    )
}

export default EditorApp;
